import React, { useCallback, useState } from "react";
import {
  AnimatePresence,
  motion,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
} from "framer-motion";
import type { MotionValue } from "framer-motion";
import TunnelBlueprint from "./TunnelBlueprint";

// The indigo half of the auth screen: a blueprint tunnel travelling in the
// background (TunnelBlueprint, decorative) with a text slider in front.
//
// Two earlier versions failed. A faux dashboard was misread as the customer's
// own analytics; a static capability list read as boring. So: rich artwork that
// cannot be mistaken for data, and real product copy at headline scale.
//
// Pages that pass their own `headline` — the password-recovery flows — get a
// single static slide with no rotation and no indicators, so their deliberately
// reassuring copy is untouched. Only Login and Register run the deck.

type BrandPanelProps = {
  headline?: string;
  subcopy?: string;
};

type Slide = {
  title: string;
  body: string;
};

const SLIDES: Slide[] = [
  {
    title: "Plan the work",
    body: "WBS, phases and activities down to the task — against a rate-loaded BOQ.",
  },
  {
    title: "Cost the 3M",
    body: "Material, manpower and machinery norms applied per task, not per project.",
  },
  {
    title: "Capture the DPR",
    body: "Daily progress recorded at the face, locked for the day and approved.",
  },
  {
    title: "Certify and compare",
    body: "Planned versus actual, deviation by resource, and the monthly top sheet.",
  },
];

const SLIDE_MS = 6000;

// framer's useAnimationFrame has no enable flag — it subscribes for the life of
// the component. Early-returning inside the callback would still leave a rAF
// loop spinning forever on the six static pages AND under reduced motion, which
// is the one place the convention says the effect must be dead. So the ticker
// lives in a child that is only mounted while the deck actually rotates.
const SlideTimer: React.FC<{
  progress: MotionValue<number>;
  paused: boolean;
  onDone: () => void;
}> = ({ progress, paused, onDone }) => {
  // Accumulating deltas ourselves is what makes pausing honest — the bar stops
  // where it stopped and resumes from there, rather than a timer that silently
  // keeps running underneath or restarts from zero.
  useAnimationFrame((_, delta) => {
    if (paused) return;
    const next = progress.get() + delta / SLIDE_MS;
    if (next >= 1) {
      progress.set(0);
      onDone();
    } else {
      progress.set(next);
    }
  });
  return null;
};

const BrandPanel: React.FC<BrandPanelProps> = ({ headline, subcopy }) => {
  const reduceMotion = useReducedMotion();
  const still = !!reduceMotion;

  // `=== undefined`, not truthiness: an intentional headline="" must still
  // suppress rotation rather than silently starting the deck.
  const isStatic = headline !== undefined;
  const slides: Slide[] = isStatic
    ? [{ title: headline as string, body: subcopy || "" }]
    : SLIDES;

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // A motion value, not state: the dot fill updates every frame and has no
  // business re-rendering React 60 times a second.
  const progress = useMotionValue(0);

  const rotates = !isStatic && !still && slides.length > 1;

  const go = useCallback(
    (i: number) => {
      progress.set(0);
      setIndex(i);
    },
    [progress],
  );

  const slide = slides[index];

  return (
    <aside
      className="pxauth__brand"
      // Someone mistyping a password next to a panel that keeps moving is the
      // exact irritation this component's history warns about.
      // Mouse, not pointer: pointerleave may never arrive after a touch tap,
      // which would strand the panel paused for good.
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      // onFocus/onBlur (not the *Capture variants) — React maps these to
      // focusin/focusout, which bubble, so descendant focus reaches the aside.
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      <div className="pxauth__brandInner">
        <TunnelBlueprint />

        <div className="pxauth__brandCopy">
          <p className="pxauth__eyebrow">Tunnel &amp; infrastructure delivery</p>

          <section
            className={`pxauth-slides${
              slides.length > 1 ? "" : " pxauth-slides--single"
            }`}
            aria-roledescription="carousel"
            aria-label="What ProcureX PMS does"
          >
            {rotates && (
              <SlideTimer
                progress={progress}
                paused={paused}
                onDone={() => setIndex((i) => (i + 1) % slides.length)}
              />
            )}
            {/* Live only once the user has taken control. While it rotates on
                its own, announcing marketing copy every 6s would interrupt
                someone part-way through the form; once paused, a dot press
                with nothing announced would be a dead end. */}
            <div
              className="pxauth-slides__viewport"
              aria-live={paused ? "polite" : "off"}
            >
              <AnimatePresence initial={false}>
                <motion.div
                  key={index}
                  className="pxauth-slides__slide"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${slides.length}: ${slide.title}`}
                  initial={still ? { opacity: 0 } : { opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={still ? { opacity: 0 } : { opacity: 0, y: -10 }}
                  // Under reduced motion a dot press still crossfades: opacity
                  // is not transform motion, and 150ms is exactly the design
                  // system's chrome budget (§7).
                  transition={{
                    duration: still ? 0.15 : 0.45,
                    ease: [0.22, 0.61, 0.36, 1],
                  }}
                >
                  <h2 className="pxauth__brandTitle">{slide.title}</h2>
                  {slide.body && (
                    <p className="pxauth__brandSub">{slide.body}</p>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {slides.length > 1 && (
              <div className="pxauth-slides__nav">
                {slides.map((item, i) => (
                  <button
                    key={item.title}
                    type="button"
                    className="pxauth-slides__dot"
                    aria-label={`Show slide ${i + 1} of ${slides.length}: ${
                      item.title
                    }`}
                    // aria-current, never aria-selected — the latter is only
                    // valid on option/tab/row/gridcell and axe flags it here.
                    aria-current={i === index ? "true" : undefined}
                    onClick={() => go(i)}
                  >
                    <span className="pxauth-slides__dotTrack">
                      {i === index && (
                        <motion.span
                          className="pxauth-slides__dotFill"
                          // scaleX off a left origin, never width.
                          style={{ scaleX: rotates ? progress : 1 }}
                        />
                      )}
                    </span>
                  </button>
                ))}

                {/* WCAG 2.2.2: content that auto-updates for more than 5s needs
                    a mechanism to pause it. Hover and focus-within are not a
                    mechanism for a screen-reader user in virtual cursor. */}
                {rotates && (
                  <button
                    type="button"
                    className="pxauth-slides__pause"
                    aria-label={
                      paused ? "Resume slide rotation" : "Pause slide rotation"
                    }
                    onClick={() => setPaused((p) => !p)}
                  >
                    <svg
                      viewBox="0 0 12 12"
                      width="10"
                      height="10"
                      aria-hidden="true"
                      fill="currentColor"
                    >
                      {paused ? (
                        <path d="M3 1.5 10 6 3 10.5z" />
                      ) : (
                        <path d="M3 1.5h2.2v9H3zM6.8 1.5H9v9H6.8z" />
                      )}
                    </svg>
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </aside>
  );
};

export default BrandPanel;
